// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { Box, Button, CircularProgress, Dialog, Fade, FormControl, FormGroup, InputLabel, MenuItem, Select, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { useTheme } from "@mui/system";
import stylesDef from "./styles";
import { useAppDispatch, useAppSelector } from "Hooks";
import { useEffect, useState } from "react";
import { GetAllBundlesThunk, GetAllCustomBundlesThunk, GetDeliverablesByBundleThunk } from "Features/bundle/bundleThunk";
import { CreateNewCustomBundleThunk } from "Features/bundle/bundleThunk";
import AddOnItem from "./AddOnItem";

interface CreateCustomBundleDialogProps {
  open: boolean;
  setOpen: (value: boolean) => void;
}

const CreateCustomBundleDialog = ({ open, setOpen }: CreateCustomBundleDialogProps) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();

  const _id = _ADMIN_ ? useAppSelector((state) => state.admin.orgId) : useAppSelector((state) => state.account.org._id);
  const { allBundles } = useAppSelector((state) => state.bundle);

  const [bundles, setBundles] = useState<string[]>([]);
  const [customBundleName, setCustomBundleName] = useState<string>('');
  const [parentBundle, setParentBundle] = useState<string>('');
  const [bundleData, setBundleData] = useState<BundleData>(null);
  const [selectedAddons, setSelectedAddons] = useState<{ _id: string, is3D: boolean }[]>([]);
  const [pointsAndAttributes, setPointsAndAttributes] = useState<boolean>(false);
  const [allSelected, setAllSelected] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);

  const onClose = () => {
    setCustomBundleName('');
    setParentBundle('');
    setBundleData(null);
    setAllSelected(false);
    setPointsAndAttributes(false);
    setSelectedAddons([]);
    setOpen(false);
  };

  const onSubmit = async () => {
    if (customBundleName === '' || parentBundle === '') {
      return;
    }
    setIsLoading(true);
    const deliverablesList = bundleData.defaultDeliverables.map((del) => {
      return {
        _id: del.id,
        is3D: false,
      }
    });
    const result = await dispatch(CreateNewCustomBundleThunk(customBundleName, selectedAddons, deliverablesList, bundleData.parentId, pointsAndAttributes));
    if (result.success) {
      await dispatch(GetAllCustomBundlesThunk(_id));
      onClose();
      setIsLoading(false);
    }
  }

  const handleAddonCheck = (event: any, id: string, is3D?: boolean) => {
    if (event.target.checked) {
      setSelectedAddons((prev) => [...prev, { _id: id, is3D: is3D || false }]);
    } else {
      setSelectedAddons((prev) => prev.filter((addon) => addon._id !== id));
    }
  }

  useEffect(() => {
    const fetchBundles = async () => {
      await dispatch(GetAllBundlesThunk());
    };

    fetchBundles();
  }, []);

  useEffect(() => {
    if (parentBundle !== '') {
      const fetchDeliverables = async () => {
        const result = await dispatch(GetDeliverablesByBundleThunk(parentBundle));
        setBundleData(result);
        setSelectedAddons([]);
      }

      fetchDeliverables();
    } else {
      setSelectedAddons([]);
      setBundleData(null);
    }
  },[parentBundle]);

  useEffect(() => {
    if (allBundles) {
      const { standard, utilities } = allBundles;
      const apiBundles = [...standard, ...utilities.underground, ...utilities.aerial];
      
      // Static bundle order
      const staticBundles = [
        '2D + Topo',
        '2D',
        'Topo',
        '3D Feature Elevation',
        'A la carte',
        'Underground A-la-carte',
        'Underground Basic',
        'Underground Heavy',
        'Basemap - Light',
        'Basemap - High Fidelity',
        'Aerial A-la-carte'
      ];
      const staticBundlesSet = new Set(staticBundles);
      const additionalBundles = apiBundles.filter(bundle => !staticBundlesSet.has(bundle));
      setBundles([...staticBundles, ...additionalBundles]);
    }
  }, [allBundles]);

  useEffect(() => {
    if (bundleData) {
      const { attributeAddOnsList } = bundleData;
      const allSelected = attributeAddOnsList.length && attributeAddOnsList.every((item) => selectedAddons.some((selected) => selected._id === item));
      setAllSelected(allSelected);
    }
  }, [selectedAddons]);

  return (
    <Dialog open={open} onClose={onClose}>
      <Box sx={{ ...styles.dialogContainer, ...(bundleData ? styles.dialogContainerWithData : {}) }}>
        <Typography sx={styles.dialogContainerTitle}>Create Custom Bundle</Typography>
        <Typography>Please choose a parent bundle and select the layers/deliverables you&apos;d like to be drafted into this new bundle.</Typography>
        <Box sx={styles.dialogInputs}>
          <TextField 
            label="Name" 
            sx={styles.inputWidth} 
            variant="standard" 
            placeholder="Custom Bundle Name" 
            value={customBundleName} 
            onChange={(event) => setCustomBundleName(event.target.value)}
          />
          <FormControl sx={styles.inputWidth} variant="standard"> 
            <InputLabel>Parent Bundle</InputLabel>
            <Select variant="standard" value={parentBundle} onChange={(event) => setParentBundle(event.target.value)}>
              {
                bundles && bundles.map((bundle) => (
                  <MenuItem key={bundle} value={bundle}>
                    {bundle}
                  </MenuItem>
                ))
              }
            </Select>
          </FormControl>
        </Box>
        <Box sx={styles.deliverablesContainer}>
          {bundleData && bundleData.additionalDeliverables.map((item) => (
            <FormGroup key={item.subtype} sx={styles.subtypeContainer}>
              <Typography variant="subtitle1" sx={styles.subtypeName}>{item.subtype}</Typography>
              {item.deliverables.map((del) => (
                <AddOnItem 
                  key={del.id} 
                  item={del}
                  handleAddonCheck={handleAddonCheck}
                  selectedAddons={selectedAddons}
                  available3D={del.has3D && parentBundle === 'Underground A-la-carte'}
                />
              ))}
            </FormGroup>
          ))}
        </Box>
        <Box sx={allSelected ? styles.buttonContainerWithAttributes : styles.buttonContainer}>
          {parentBundle === 'Aerial A-la-carte' && allSelected ?
            (<Box sx={styles.pointsAndAttributesContainer}>
              <Typography sx={styles.outputText}>
                Output:
              </Typography>
              <ToggleButtonGroup
                value={pointsAndAttributes}
                exclusive
                sx={styles.pointsToggle}
                onChange={(_event, value) => {
                  setPointsAndAttributes(value);
                }}
                aria-label="attribute toggle"
              >
                <ToggleButton 
                  value={false}
                  sx={!pointsAndAttributes ? styles.selected : styles.notSelected}
                  disabled={!pointsAndAttributes}
                >
                  Location
                </ToggleButton>
                <ToggleButton 
                  value={true}
                  sx={pointsAndAttributes ? styles.selected : styles.notSelected}
                  disabled={pointsAndAttributes}
                >
                  Location & Dimension
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>) : null
          }
          <Box sx={styles.buttonGroup}>
            <Button sx={styles.closeButton} onClick={onClose}>Close</Button>
            <Button 
              color="primary" 
              variant="contained" 
              onClick={onSubmit}
              disabled={customBundleName === '' || parentBundle === '' || isLoading}
            >
              <Fade in={isLoading}>
                <CircularProgress size={24} sx={styles.buttonProgress}/>
              </Fade>
              <Fade in={!isLoading}>
                <span>Save Changes</span>
              </Fade>
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}

export default CreateCustomBundleDialog;
